/* eslint-disable unicorn/no-array-callback-reference, unicorn/no-array-method-this-argument */
import { InternalServerErrorException, NotFoundException } from '@nestjs/common';
import { instanceToInstance } from 'class-transformer';
import cleanDeep from 'clean-deep';
import { dot } from 'dot-object';
import { isEmpty, isEqual, isObjectLike, keys, omit, pick } from 'lodash';
import { Filter } from 'mongodb';
import { Types } from 'mongoose';

import { EventType, PusherEventType } from '../enumerations';
import {
  BaseOutputModel,
  CreateInputModel,
  DeleteInputModel,
  EventInputModel,
  InfoInputModel,
  ListInputModel,
  ListOutputModel,
  UpdateInputModel,
} from '../models';
import { EventRepository } from '../repositories';
import { QueryOptions, Request } from '../types';
import { PusherService } from './pusher.service';

export abstract class EventService<T extends BaseOutputModel> {
  protected constructor(
    public readonly eventRepository: EventRepository<T>,
    public readonly basePusherService: PusherService,
    public readonly outputModelInstantiator: (document: object | null) => T,
    public readonly instanceName: string,
    public readonly searchProperties: (keyof T)[] = [],
    public readonly prerequisites?: Filter<any>
  ) {}

  public async create(createInputModel: CreateInputModel, request: Request): Promise<T> {
    const gid = (createInputModel as any).gid || new Types.ObjectId();
    const author = request.user?.id || request.body?.id || gid;
    const eventInputModel = new EventInputModel(gid, { ...omit(createInputModel, 'gid'), ...this.prerequisites, deleted: false }, EventType.Create, author);
    const acknowledged = await this.eventRepository.create(eventInputModel);
    if (!acknowledged) throw new InternalServerErrorException('Could not create document.');
    const document = await this.eventRepository.findById(gid);
    const data = instanceToInstance(this.outputModelInstantiator(document));
    return this.basePusherService.emit(data, PusherEventType.Created, this.instanceName, request);
  }

  public async delete(deleteInputModel: DeleteInputModel, request: Request): Promise<void> {
    const filter = { ...this.prerequisites, deleted: false };
    const document = await this.eventRepository.findById(deleteInputModel.id, deleteInputModel.force ? omit(filter, 'deleted') : filter);
    if (isEmpty(document)) throw new NotFoundException('Document not found.');
    const author = request.user?.id || request.body?.id;
    const acknowledged = await (deleteInputModel.force
      ? this.eventRepository.findByIdAndDelete(deleteInputModel.id)
      : this.eventRepository.create(new EventInputModel(deleteInputModel.id, { deleted: true }, EventType.Delete, author)));
    if (!acknowledged) throw new InternalServerErrorException('Could not delete document.');
    const data = instanceToInstance(this.outputModelInstantiator(document));
    await this.basePusherService.emit(data, PusherEventType.Deleted, this.instanceName, request);
  }

  public async info(infoInputModel: InfoInputModel, filter: Filter<any> = {}): Promise<T> {
    const query = { ...filter, ...this.prerequisites, deleted: false };
    const document = infoInputModel.timestamp
      ? await this.eventRepository.findByIdOn(infoInputModel.id, infoInputModel.timestamp, query)
      : await this.eventRepository.findById(infoInputModel.id, query);
    if (isEmpty(document)) throw new NotFoundException('Document not found.');
    return instanceToInstance(this.outputModelInstantiator(document));
  }

  public async list(listInputModel: ListInputModel, filter: Filter<any> = {}, { projection }: QueryOptions<any> = {}): Promise<ListOutputModel<T>> {
    const query = { ...filter, ...this.prerequisites, deleted: false };
    if (!isEmpty(this.searchProperties) && !isEmpty(listInputModel.search))
      query.$or = this.searchProperties.map((property) => ({ [property]: new RegExp(listInputModel.search as string, 'i') }));
    const [count, documents] = await Promise.all([this.eventRepository.count(query), this.eventRepository.find(query, { ...listInputModel, projection })]);
    const models = documents.map((document) => instanceToInstance(this.outputModelInstantiator(document)));
    return new ListOutputModel(count, models);
  }

  public async update(updateInputModel: UpdateInputModel, request: Request): Promise<T> {
    const filter = { ...this.prerequisites, deleted: false };
    const projection = { deleted: 0, created_at: 0, created_by: 0, updated_at: 0, updated_by: 0 };
    let document = await this.eventRepository.findById(updateInputModel.id, filter, { projection });
    if (isEmpty(document)) throw new NotFoundException('Document not found.');
    const { difference, former } = this.getDifferenceAndFormer(document, omit(updateInputModel, 'id'));
    if (!isEmpty(difference)) {
      const author = request.user?.id || request.body?.id;
      const eventInputModel = new EventInputModel(updateInputModel.id, difference, EventType.Update, author, isEmpty(former) ? null : former);
      const acknowledge = await this.eventRepository.create(eventInputModel);
      if (!acknowledge) throw new InternalServerErrorException('Could not update document.');
      document = await this.eventRepository.findById(updateInputModel.id);
    }
    const data = instanceToInstance(this.outputModelInstantiator(document));
    return this.basePusherService.emit(data, PusherEventType.Updated, this.instanceName, request);
  }

  private getDifferenceAndFormer(document: any, update: any): any {
    const difference = cleanDeep(this.makeDifference(document, update), { nullValues: false });
    const former = pick(document, keys(dot(difference)));
    return { difference, former };
  }

  private makeDifference(lhs: any, rhs: any): any {
    if (isEqual(lhs, rhs)) return {};
    if (Array.isArray(lhs) || Array.isArray(rhs)) return rhs;
    if (lhs instanceof Date || rhs instanceof Date) return rhs;
    if (!isObjectLike(lhs) || !isObjectLike(rhs)) return rhs;
    return Object.entries(rhs).reduce((accumulator: any, [key, value]: [string, any]) => {
      const original = lhs[key];
      if (original) {
        const difference = this.makeDifference(original, value);
        if (isEmpty(difference) && !(difference instanceof Date) && !Array.isArray(difference) && (isEmpty(original) || !isEmpty(value))) return accumulator;
        accumulator[key] = difference;
      } else accumulator[key] = value;
      return accumulator;
    }, {});
  }
}
